import { ArrowForward } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    accordionSummaryClasses,
    Box,
    Typography,
    type AccordionProps,
    type AccordionSummaryProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ReferenceManyField } from "ra-ui-materialui";
import * as React from "react";
import { CategoryBreadcrumb } from "../../layout/category-breadcrumb";
import { fullName, type OfferVariant } from "../../models";
import { TagGrid } from "../tag/grid";
import { useWebsite } from "../../hooks";

const SpecAccordion = styled((props: AccordionProps) => <Accordion disableGutters elevation={0} square {...props} />)(({ theme, expanded }) => ({
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": {
        borderBottom: 0,
    },
    "&::before": {
        display: "none",
    },
    flexGrow: expanded ? 1 : 0,
}));

const SpecAccordionSummary = styled((props: AccordionSummaryProps) => (
    <AccordionSummary expandIcon={<ArrowForward sx={{ fontSize: "0.9rem" }} />} {...props} />
))(({ theme }) => ({
    backgroundColor: "rgba(0, 0, 0, .03)",
    flexDirection: "row-reverse",
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
        transform: "rotate(90deg)",
    },
    [`& .${accordionSummaryClasses.content}`]: {
        marginLeft: theme.spacing(1),
    },
    ...theme.applyStyles("dark", {
        backgroundColor: "rgba(255, 255, 255, .05)",
    }),
}));

const SpecAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

interface DescriptionProps {
    description: string;
    variant: OfferVariant;
    expandedIndex: [number, React.Dispatch<React.SetStateAction<number>>];
    detailIndex: [number, React.Dispatch<React.SetStateAction<number>>];
}

export function Description({ description, variant, expandedIndex, detailIndex }: DescriptionProps) {
    const [expanded, setExpanded] = expandedIndex;
    const { accountId } = useWebsite();
    const handleChange = (panel: number) => () => {
        setExpanded(panel);
        if (panel > 0) {
            detailIndex[1](panel - 1);
        }
    };
    return (
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <SpecAccordion expanded={expanded === 0} onChange={handleChange(0)}>
                <SpecAccordionSummary aria-controls="description-content">
                    <Typography component="span">Description</Typography>
                </SpecAccordionSummary>
                <SpecAccordionDetails sx={{ maxHeight: 408, overflow: "scroll" }}>
                    <Typography>{description}</Typography>
                </SpecAccordionDetails>
            </SpecAccordion>
            {variant.details.map((detail, i) => {
                return (
                    <SpecAccordion expanded={expanded === i + 1} onChange={handleChange(i + 1)}>
                        <SpecAccordionSummary aria-controls={`spec-${i}-content`}>
                            <Typography component="span">{fullName({ name: detail.product, variant: detail.variant })}</Typography>
                        </SpecAccordionSummary>
                        <SpecAccordionDetails sx={{ maxHeight: 496, overflow: "scroll" }}>
                            <CategoryBreadcrumb logoSize={36} fontSize="1rem" categoryId={detail.categoryId} sx={{ paddingTop: 0.5, paddingBottom: 0.75 }} />
                            <ReferenceManyField
                                queryOptions={{ enabled: expanded === i + 1 }}
                                reference="tag"
                                record={{ id: accountId }}
                                target="accountId"
                                filter={{
                                    brandId: detail.brandId,
                                    productId: detail.productId,
                                    "select@": "*,...specification(filter:name)",
                                }}
                                // queryOptions={{ meta: { embed: ["filter"] } }}
                            >
                                <TagGrid />
                            </ReferenceManyField>
                        </SpecAccordionDetails>
                    </SpecAccordion>
                );
            })}
        </Box>
    );
}
